import React from 'react';
import useModal from '../../../hooks/useModal';
import { CloneTrainingModal } from '../components/modals/CloneTrainingModal';
import { PublishTrainingModal } from '../components/modals/PublishTrainingModal';
import { RemoveTrainingStepModal } from '../components/modals/RemoveTrainingStepModal';
import { TrainingEasyLinkModal } from '../components/modals/TrainingEasyLinkModal';
import { UnpublishTrainingModal } from '../components/modals/UnpublishTrainingModal';
import { EditCourseStepModal } from '../components/EditCourseStepModal';
import { AddCourseStepModal } from '../components/AddCourseStepModal';
import { CourseStep } from '../models/course';

type ModalType =
  | 'addStep'
  | 'editStep'
  | 'removeStep'
  | 'easyLink'
  | 'publishModal'
  | 'unpublishModal'
  | 'cloneTraining';

type AddStepPayload = {
  trainingId: string;
  position: number;
  isOwner: boolean;
  onSuccess?: (step: CourseStep) => void | Promise<void>;
};

type EditStepPayload = {
  trainingId: string;
  existingStep: CourseStep;
  isOwner: boolean;
  onSuccess?: (step: CourseStep) => void | Promise<void>;
};

type RemoveStepPayload = {
  stepName: string;
  remove: () => Promise<void>;
};

type EasyLinkPayload = {
  trainingId: string;
  trainingName: string;
  orgId: string;
};

type HandlerPayload<T = null> = {
  handler: (data?: T) => Promise<void>;
};

type CourseCopyPayload<T = null> = {
  handler: (data?: T) => Promise<void>;
  title?: string;
  isInPathway?: boolean;
};

type Payload =
  | AddStepPayload
  | EditStepPayload
  | RemoveStepPayload
  | EasyLinkPayload
  | HandlerPayload
  | HandlerPayload<string>
  | CourseCopyPayload<string>;

export const useCourseModal = () => {
  const [modal, openModal, dismissModal] = useModal((type: ModalType, payload: Payload, dismissHandler: () => void) => {
    if (type === 'addStep') {
      const { trainingId, position, onSuccess } = payload as AddStepPayload;
      return (
        <AddCourseStepModal
          courseId={trainingId}
          stepOrder={position}
          handleDismiss={dismissHandler}
          onSuccess={onSuccess}
        />
      );
    }

    if (type === 'editStep') {
      const { trainingId, existingStep, onSuccess } = payload as EditStepPayload;
      return (
        <EditCourseStepModal
          courseId={trainingId}
          stepOrder={existingStep.order}
          handleDismiss={dismissHandler}
          onSuccess={onSuccess}
          existingStep={existingStep}
        />
      );
    }

    if (type === 'removeStep') {
      const { stepName, remove } = payload as RemoveStepPayload;
      return <RemoveTrainingStepModal subtext={stepName} handleSubmit={remove} handleDismiss={dismissHandler} />;
    }

    if (type === 'easyLink') {
      const { trainingId, trainingName, orgId } = payload as EasyLinkPayload;
      return (
        <TrainingEasyLinkModal
          trainingId={trainingId}
          trainingName={trainingName}
          orgId={orgId}
          handleDismiss={dismissHandler}
        />
      );
    }

    if (type === 'publishModal') {
      const { handler } = payload as HandlerPayload;
      return <PublishTrainingModal trainingType="Course" publish={handler} handleDismiss={dismissHandler} />;
    }

    if (type === 'unpublishModal') {
      const { handler } = payload as HandlerPayload;
      return <UnpublishTrainingModal trainingType="Course" unpublish={handler} handleDismiss={dismissHandler} />;
    }

    if (type === 'cloneTraining') {
      const { handler } = payload as CourseCopyPayload<string>;
      return <CloneTrainingModal trainingType="Course" handleCopy={handler} handleDismiss={dismissHandler} />;
    }

    return null;
  });

  const openAddStepModal = (
    trainingId: string,
    position: number,
    isOwner: boolean,
    onSuccess?: (step: CourseStep) => void | Promise<void>
  ) => openModal('addStep', { trainingId, position, isOwner, onSuccess });

  const openEditStepModal = (
    trainingId: string,
    existingStep: CourseStep,
    isOwner: boolean,
    onSuccess?: (step: CourseStep) => void | Promise<void>
  ) => openModal('editStep', { trainingId, existingStep, isOwner, onSuccess });

  const openRemoveStepModal = (stepName: string, remove: () => Promise<void>) =>
    openModal('removeStep', { stepName, remove });

  const openEasyLinkModal = (trainingId: string, trainingName: string, orgId: string) =>
    openModal('easyLink', { trainingId, trainingName, orgId });

  const openPublishModal = (handler: () => Promise<void>) => openModal('publishModal', { handler });

  const openUnpublishModal = (handler: () => Promise<void>) => openModal('unpublishModal', { handler });

  const openCopyModal = (handler: (name: string) => Promise<void>, title?: string, isInPathway = false) =>
    openModal('cloneTraining', { handler, title, isInPathway });

  return {
    modal,
    openAddStepModal,
    openEditStepModal,
    openRemoveStepModal,
    openEasyLinkModal,
    openPublishModal,
    openUnpublishModal,
    openCopyModal,
    dismissModal,
  };
};
